import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  isAnimating,
  isProcessingSlowMotion,
  isSourceImageProcessing,
} from "@repo/api/utils/composition";
import { IconAlertCircle } from "@tabler/icons-react";
import { useMemo } from "react";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { SourceImageDto } from "@repo/api/source-image";

type Props = {
  timelineItems: CreativeProjectTimelineItem[];
  animatedImages: Record<string, AnimatedImageDto>;
  sourceImages: Record<string, SourceImageDto>;
};

export default function VideoPreviewHelperText({
  timelineItems,
  animatedImages,
  sourceImages,
}: Props) {
  const status = useMemo(() => {
    const statuses = timelineItems.map((timelineItem) => {
      const startImage = sourceImages[timelineItem.startSourceImageExternalId];
      const endImage = timelineItem.endSourceImageExternalId
        ? sourceImages[timelineItem.endSourceImageExternalId]
        : undefined;
      const animatedImage = timelineItem.animatedImageExternalId
        ? animatedImages[timelineItem.animatedImageExternalId]
        : undefined;

      if (animatedImage && isAnimating(animatedImage)) {
        return "ANIMATING";
      }

      if (
        (startImage && isSourceImageProcessing(startImage)) ||
        (endImage && isSourceImageProcessing(endImage))
      ) {
        return "GENERATING_IMAGES";
      }

      if (animatedImage && isProcessingSlowMotion(animatedImage)) {
        return "SLOW_MOTION_PROCESSING";
      }

      if (animatedImage?.status === "FAILED") {
        return "ANIMATION_FAILED";
      }

      if (startImage?.status === "FAILED" || endImage?.status === "FAILED") {
        return "IMAGE_GENERATION_FAILED";
      }

      if (animatedImage?.smStatus === "FAILED") {
        return "SLOW_MOTION_FAILED";
      }

      return "NONE";
    });

    if (statuses.some((status) => status === "ANIMATING")) {
      return "ANIMATING";
    }

    if (statuses.some((status) => status === "GENERATING_IMAGES")) {
      return "GENERATING_IMAGES";
    }

    if (statuses.some((status) => status === "SLOW_MOTION_PROCESSING")) {
      return "SLOW_MOTION_PROCESSING";
    }

    if (statuses.some((status) => status === "ANIMATION_FAILED")) {
      return "ANIMATION_FAILED";
    }

    if (statuses.some((status) => status === "IMAGE_GENERATION_FAILED")) {
      return "IMAGE_GENERATION_FAILED";
    }

    if (statuses.some((status) => status === "SLOW_MOTION_FAILED")) {
      return "SLOW_MOTION_FAILED";
    }

    return "NONE";
  }, [timelineItems, animatedImages, sourceImages]);

  let component = null;

  if (status === "IMAGE_GENERATION_FAILED") {
    component = (
      <div className="flex items-center">
        <IconAlertCircle size={16} className="mr-2 shrink-0 text-error" />
        <p className="line-clamp-1 text-text-secondary">
          Image generation failed.
        </p>
      </div>
    );
  }

  if (status === "ANIMATION_FAILED") {
    component = (
      <div className="flex items-center">
        <IconAlertCircle size={16} className="mr-2 shrink-0 text-error" />
        <p className="line-clamp-1 text-text-secondary">Animation failed.</p>
      </div>
    );
  }

  if (status === "SLOW_MOTION_FAILED") {
    component = (
      <div className="flex items-center">
        <IconAlertCircle size={16} className="mr-2 shrink-0 text-error" />
        <p className="line-clamp-1 text-text-secondary">
          Smooth speed change failed.
        </p>
      </div>
    );
  }

  if (status === "ANIMATING") {
    component = (
      <div className="flex items-center">
        <LoadingSpinner size={16} className="mr-2 shrink-0" />
        <p className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-sm font-medium text-transparent">
          Animating images...
        </p>
      </div>
    );
  }

  if (status === "GENERATING_IMAGES") {
    component = (
      <div className="flex items-center">
        <LoadingSpinner size={16} className="mr-2 shrink-0" />
        <p className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-sm font-medium text-transparent">
          Generating images...
        </p>
      </div>
    );
  }

  if (status === "SLOW_MOTION_PROCESSING") {
    component = (
      <div className="flex items-center">
        <LoadingSpinner size={16} className="mr-2 shrink-0" />
        <p className="line-clamp-1 text-text-secondary">
          Generating smooth speed change...
        </p>
      </div>
    );
  }

  return (
    <div className="absolute top-full right-0 left-0 mt-17 flex items-center justify-center text-sm">
      {component}
    </div>
  );
}
